/**
 * useAccountDashboard — the user's account-overview board (which widgets are
 * on their account page and where). Shares its shape with the GAS account Dash
 * through @techstack/core's AccountDashboardConfig.
 *
 * Storage is two-tier, the same split [[useNavPrefs]] uses:
 *   - localStorage is the FIRST PAINT source. The board renders before any
 *     network call resolves; without a local mirror every reload flashes the
 *     shipped default layout and then reshuffles under the cursor.
 *   - `user_preferences.account_dashboard` is the SOURCE OF TRUTH — it follows
 *     the user across devices, which is the point of arranging on a laptop and
 *     opening on a phone.
 *
 * Writes are optimistic and debounced. A drag fires a stream of layout changes;
 * sending each one would be a PUT per animation frame. The board moves on the
 * gesture, the save follows 700ms after it stops.
 *
 * The layout is per USER, not per account: you arrange the account page once
 * and every account you open uses it. Per-account layouts sound appealing and
 * are a trap — account #2 onwards would open on the shipped default forever.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  accountBoardPreferenceType, defaultAccountBoard, normalizeAccountDashboard,
} from '@techstack/core';
import userPreferencesService from '../services/userPreferences';

export const ACCOUNT_DASHBOARD_TYPE = 'account_dashboard';
const SAVE_DEBOUNCE_MS = 700;

/*
 * ONE SET OF KEYS PER BOARD.
 *
 * A tab is a board now, so the storage that held "the" board holds one per
 * board key. The overview deliberately keeps the ORIGINAL type and localStorage
 * key: every board arranged before this existed lives there, and a rename
 * would silently reset every rep to the shipped default.
 */
const localKeyFor = (boardKey) => (boardKey === 'overview'
  ? 'tekstak.accountDashboard'
  : `tekstak.accountBoard.${boardKey}`);

function readLocal(boardKey) {
  try {
    const raw = localStorage.getItem(localKeyFor(boardKey));
    // No stored value is NOT the same as a corrupt one: both end at the
    // default board, but only the corrupt case should overwrite what the
    // server might still hand back.
    return raw ? normalizeAccountDashboard(raw, 'tekstak') : null;
  } catch {
    return null;
  }
}

function writeLocal(boardKey, config) {
  try {
    localStorage.setItem(localKeyFor(boardKey), JSON.stringify(config));
  } catch {
    /* private mode / quota — the server copy still stands */
  }
}

function hasAccount() {
  try {
    return !!userPreferencesService.getAuthToken();
  } catch {
    return false;
  }
}

export function useAccountDashboard(boardKey = 'overview') {
  const prefType = accountBoardPreferenceType(boardKey);
  const [config, setConfig] = useState(
    () => readLocal(boardKey) || defaultAccountBoard('tekstak', boardKey),
  );
  const [saveState, setSaveState] = useState('idle');
  const [loaded, setLoaded] = useState(false);

  const latest = useRef(config);
  const timer = useRef(null);
  // True once the user has touched the board in this session. A server copy
  // that lands mid-edit must not stomp the arrangement under their hands.
  const dirty = useRef(false);

  const send = useCallback(async (next) => {
    if (!hasAccount()) return;
    setSaveState('saving');
    try {
      await userPreferencesService.updatePreference(prefType, next);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }, [prefType]);

  // Pull the server copy once. Deliberately not wired to AuthContext — the
  // account page renders in shells that don't mount one, and a layout
  // preference is not worth making the page un-mountable over.
  useEffect(() => {
    if (!hasAccount()) {
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    userPreferencesService
      .fetchPreference(prefType)
      .then((value) => {
        if (cancelled || dirty.current) return;
        // A never-customized user gets `null` back; keep the shipped default
        // rather than normalizing null into an empty board.
        if (value) {
          const server = normalizeAccountDashboard(value, 'tekstak');
          latest.current = server;
          setConfig(server);
          writeLocal(boardKey, server);
        }
        setLoaded(true);
      })
      .catch(() => {
        // Offline or preferences down — the local mirror stands. A board that
        // stays arranged beats one that reverts to defaults mid-flight.
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [prefType, boardKey]);

  const update = useCallback(
    (next) => {
      dirty.current = true;
      latest.current = next;
      setConfig(next);
      writeLocal(boardKey, next);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        void send(latest.current);
      }, SAVE_DEBOUNCE_MS);
    },
    [send, boardKey],
  );

  const reset = useCallback(() => {
    update(defaultAccountBoard('tekstak', boardKey));
  }, [update, boardKey]);

  const retry = useCallback(() => {
    void send(latest.current);
  }, [send]);

  // Flush a pending debounce when the tab hides. A client-side navigation lets
  // the timer fire anyway; closing the tab kills it, and losing the last drag
  // of an arranging session is exactly the change the user cared about.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden' && timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
        void send(latest.current);
      }
    };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [send]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return { config, setConfig: update, reset, retry, saveState, loaded };
}

export default useAccountDashboard;
